import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Rectangle,
} from "recharts";
import { useNavigate } from "react-router-dom";

/**
 * Weekly quotation summary chart
 * X‑axis: week range label (week_start → week_end, in Asia/Kolkata)
 * Series:
 *   - Bar (left Y): quotation_count
 *   - Line (right Y): total_price_sum (₹, compact on axis; full in tooltip)
 *
 * Expects props.data like:
 * [
 *   {
 *     week_start: "2025-10-05T18:30:00.000Z",
 *     week_end:   "2025-10-11T18:30:00.000Z",
 *     quotation_count: 11,
 *     total_price_sum: 5409333.07
 *   }, ...
 * ]
 */

// -------- Formatters (IST / en-IN) --------
const timeZone = "Asia/Kolkata" as const;
const fmtDate = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone,
});
const fmtDateShort = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  timeZone,
});
const fmtInt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const fmtMoneyAxis = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  notation: "compact",
  maximumFractionDigits: 2,
});
const fmtMoneyFull = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

// Helpers to build a clean week label and inclusive end date for display
function inclusiveEndMs(endIso: string) {
  // Back off by 1ms so that 00:00 at start of next day becomes the previous day for formatting
  return new Date(endIso).getTime() - 1;
}

function weekLabel(startIso: string, endIso: string) {
  const s = new Date(startIso);
  const e = new Date(inclusiveEndMs(endIso));
  const sLabel = fmtDateShort.format(s); // e.g. "06 Oct"
  const eLabel = fmtDateShort.format(e); // e.g. "12 Oct"

  const [sDay, sMon] = sLabel.split(" ");
  const [eDay, eMon] = eLabel.split(" ");
  // If months are same → "06–12 Oct" otherwise → "30 Sep–06 Oct"
  return sMon === eMon ? `${sDay}–${eDay} ${eMon}` : `${sDay} ${sMon}–${eDay} ${eMon}`;
}

// -------- Types --------
export type WeekRow = {
  week_start: string; // ISO in UTC
  week_end: string;   // ISO in UTC (exclusive)
  quotation_count: number;
  total_price_sum: number;
};

export interface OrderWeekDataProps {
  data: WeekRow[];
}

export default function OrderWeekData({ data }: OrderWeekDataProps) {
  const navigate = useNavigate();

  const chartData = useMemo(() => {
    if (!Array.isArray(data)) return [] as any[];
    return [...data]
      .filter((d) => d && d.week_start && d.week_end)
      .sort((a, b) => new Date(a.week_start).getTime() - new Date(b.week_start).getTime())
      .map((d) => {
        const startMs = new Date(d.week_start).getTime();
        const endMsInc = inclusiveEndMs(d.week_end);
        return {
          // keep originals
          ...d,
          // normalized/camel keys used by recharts
          label: weekLabel(d.week_start, d.week_end),
          quotationCount: d.quotation_count,
          totalPriceSum: d.total_price_sum,
          // handy for navigation
          startMs,
          endMs: endMsInc,
        };
      });
  }, [data]);

  const handlePointClick = (payload: any) => {
    if (!payload) return;
    const { startMs, endMs } = payload;
    if (!startMs || !endMs) return;
    // Use query params so backend can filter by [start,end] (inclusive)
    navigate(`/admin/quotation_list?granularity=week&start=${startMs}&end=${endMs}`);
  };

  if (!chartData.length) {
    return (
      <div style={{ padding: 16, textAlign: "center", color: "#666" }}>
        No weekly data available.
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 12 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" interval={0} height={48} tickLine={false} />

          {/* Left Y: counts */}
          <YAxis yAxisId="left" width={52} allowDecimals={false} tickFormatter={(v) => fmtInt.format(v as number)} />

          {/* Right Y: money */}
          <YAxis yAxisId="right" orientation="right" width={84} tickFormatter={(v) => fmtMoneyAxis.format(v as number)} />

          <Tooltip
            formatter={(value: any, name: any) => {
              if (name === "quotationCount") return [fmtInt.format(value), "Quotations"];
              if (name === "totalPriceSum") return [fmtMoneyFull.format(value), "Total Amount"];
              return [value, name];
            }}
            labelFormatter={(_, payload) => {
              const p = payload && payload[0] && (payload[0] as any).payload;
              if (!p) return "Week";
              const s = fmtDate.format(new Date(p.week_start));
              const e = fmtDate.format(new Date(p.endMs));
              return `Week: ${s} – ${e}`;
            }}
          />

          <Legend />

          {/* Bars: discrete counts are easier to read as columns */}
          <Bar
            yAxisId="left"
            dataKey="quotationCount"
            name="Quotations"
            fill="#B3CDAD"
            radius={[6, 6, 0, 0]}
            activeBar={<Rectangle fill="#FFB3C7" stroke="#7D2E68" />}
            onClick={(d: any, _i: any) => handlePointClick((d as any)?.payload)}
          />

          {/* Line: revenue trend on its own scale */}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="totalPriceSum"
            name="Total Amount"
            strokeWidth={3}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
            onClick={(d: any, _i: any) => handlePointClick((d as any)?.payload)}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
